import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { CampaignOData } from '@bingads-webui-react/campaign-odata';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { QueryParam, withScope } from '@bingads-webui-react/scope-service';
import { Mode } from '@bingads-webui-react/app-layout-container';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withHotKeys, ShortcutType } from '@bingads-webui-react/hot-keys';
import { getCampaignOdataConfig } from '@bingads-webui-campaign-react/campaign-entity-search';
import { setCampaignTypes } from '@bingads-webui-campaign-react/with-campaign-type';

import {
  convertToCampaign,
  convertToAdGroup,
  getFilteredCampaignTypeFromRawFilter,
  getFilteredCampaignStatusFromRawFilter,
  getFilteredAdGroupStatusFromRawFilter,
  convertFilteredCampaignTypeToFilter,
  convertFilteredCampaignStatusToFilter,
  convertFilteredAdGroupStatusToFilter,
  shellTips,
  Scope,
  CampaignType
} from '@bingads-webui-campaign-react/shell-ui-common';
import {
  StyledCampaignMainMenuView,
  SINGLE_CAMPAIGN_VIEW_AD_GROUP_COUNT_THRESHOLD
} from './campaign-main-menu-view';
import {
  updateUrlOnCampaignTypeChange,
  updateUrlOnTreeNodeChange,
  getSmartPath
} from './helpers/router-helper';

const MAXIMUM_AD_GROUP_COUNT_UNDER_ACCOUNT = 20000;

class CampaignMainMenuAdapter extends React.PureComponent {
  constructor(props) {
    super(props);

    this.campaignMainMenuPerfMarker = props.appPerfMarker.createChild('Campaigns_Main_Menu');
    this.markAction('willInit');
    this.mainMenuScenario = props.instrumentation.mainMenuScenario.create();
    this.changeScopeActivity = this.mainMenuScenario.changeScopeActivity.create();

    this.loadedAdGroupsByCampaignId = {};
    this.previousCampaigns = [];
  }

  onWaterfallFilterChange = (update) => {
    this.props.updateWaterfallFilter({
      filterType: WaterfallFilterType.CampaignStatus,
      filter: convertFilteredCampaignStatusToFilter(update.campaign),
    });

    this.props.updateWaterfallFilter({
      filterType: WaterfallFilterType.AdGroupStatus,
      filter: convertFilteredAdGroupStatusToFilter(update.adGroup),
    });
  };

  onCampaignTypeChange = (node, shouldUpdateUrl = true) => {
    this.changeScopeActivity.trace({
      scope: Scope.Account,
      component: 'Main Menu',
      campaignType: node.campaignType,
    });

    if (node.campaignType === CampaignType.Smart) {
      window.location.href = getSmartPath(this.props.scope);
    } else {
      let scopeChangeAccepted = true;
      if (shouldUpdateUrl) {
        scopeChangeAccepted = updateUrlOnCampaignTypeChange(this.props.scope);
      }

      if (scopeChangeAccepted) {
        this.props.updateWaterfallFilter({
          filterType: WaterfallFilterType.CampaignType,
          filter: convertFilteredCampaignTypeToFilter(node.campaignType),
        });
      }
    }
  };

  onTreeNodeChange = (node) => {
    this.changeScopeActivity.trace({
      scope: node.parentId ? Scope.AdGroup : Scope.Campaign,
      component: 'Main Menu',
      campaignType: this.previousCampaignType,
    });

    return updateUrlOnTreeNodeChange(node, this.props.scope);
  };

  getProps = () => {
    const selectedCampaignType
      = getFilteredCampaignTypeFromRawFilter(this.props[WaterfallFilterType.CampaignType]);

    const statusWaterfallFilter = {
      campaign:
        getFilteredCampaignStatusFromRawFilter(this.props[WaterfallFilterType.CampaignStatus]),
      adGroup:
        getFilteredAdGroupStatusFromRawFilter(this.props[WaterfallFilterType.AdGroupStatus]),
    };

    const {
      scope: {
        values: {
          aid, campaignId, adGroupId,
        },
      },
    } = this.props;

    return {
      ...this.props,
      onTreeNodeChange: this.onTreeNodeChange,
      statusWaterfallFilter,
      onWaterfallFilterChange: this.onWaterfallFilterChange,
      selectedCampaignType,
      onCampaignTypeChange: this.onCampaignTypeChange,
      accountId: aid,
      selectedCampaignId: campaignId,
      selectedAdGroupId: adGroupId,
      mainMenuScenario: this.mainMenuScenario,
      mainMenuScenarioLogger: this.mainMenuScenarioLogger,
    };
  };

  getScope = ({ campaignType, campaignId, adGroupId }) => {
    if (adGroupId === this.previousAdGroupId
      && campaignId === this.previousCampaignId
      && campaignType === this.previousCampaignType) {
      return null; // return null means no change.
    }

    let scope = Scope.Account;
    if (adGroupId) {
      scope = Scope.AdGroup;
    } else if (campaignId) {
      scope = Scope.Campaign;
    }

    return scope;
  };

  logMainMenuActivities = (props) => {
    const {
      selectedCampaignId: campaignId,
      selectedAdGroupId: adGroupId,
      selectedCampaignType: campaignType,
    } = props;

    const scope = this.getScope({ campaignId, adGroupId, campaignType });

    // Only log when there is change.
    if (scope) {
      this.changeScopeActivity.trace({
        scope,
        component: 'All',
        campaignType,
      });
    }

    this.previousCampaignType = campaignType;
    this.previousCampaignId = campaignId;
    this.previousAdGroupId = adGroupId;
  };

  markAction = (action) => {
    if (this.campaignMainMenuPerfMarker && action) {
      this.campaignMainMenuPerfMarker[action]();
    }
  };

  render() {
    if (this.props.mode === Mode.Hidden) {
      return null;
    }
    const props = this.getProps();

    this.logMainMenuActivities(props);

    const campaignsLoadFallback = (
      <StyledCampaignMainMenuView
        {...this.getProps()}
        campaigns={[]}
        isLoadingData
      />
    );

    const adGroupLoadFallback = (
      <StyledCampaignMainMenuView
        {...this.getProps()}
        campaigns={this.previousCampaigns}
        isLoadingData
      />
    );

    this.markAction('willFetchData');
    this.props.scope.acid = this.props.serverContext.CurrentAccount.AdvertiserCustomerID;
    return (
      <CampaignOData
        call={({ currentAccount }) => currentAccount.Campaigns.get(getCampaignOdataConfig())}
        escapeHatch={({ currentAccount }) => (currentAccount ? undefined : { value: [] })}
        fallback={campaignsLoadFallback}
        onError={() => {
          notificationApi.showWarning(props.i18n.getString(shellTips.shellDataError));
          return campaignsLoadFallback;
        }}
      >
        {
          (campaignLoadResult) => {
            setCampaignTypes(_.get(campaignLoadResult, 'value', []));
            return (
              <CampaignOData
                call={
                  ({ currentCampaign }) => currentCampaign.AdGroups.get({
                    top: MAXIMUM_AD_GROUP_COUNT_UNDER_ACCOUNT,
                    select: ['Id', 'Name', 'Status'],
                    orderby: { Name: 1 },
                  })
                }
                escapeHatch={({ currentCampaign }) => (currentCampaign ? undefined : { value: [] })}
                fallback={adGroupLoadFallback}
                onError={() => {
                  notificationApi.showWarning(props.i18n.getString(shellTips.shellDataError));
                  return adGroupLoadFallback;
                }}
              >
                {
                  (adGroupLoadResult) => {
                    const adGroups = adGroupLoadResult.value.map(adGroupInOdata =>
                      convertToAdGroup({ ...adGroupInOdata, Campaign: { Id: props.selectedCampaignId } }));

                    const mainMenuCampaigns
                      = campaignLoadResult.value.map((campaignInOdata) => {
                        const campaign = convertToCampaign(campaignInOdata);

                        if (props.selectedCampaignId === campaign.id) {
                          // Only need to cache AdGroups of Campaign which belongs to multi nodes tree.
                          if (adGroups.length < SINGLE_CAMPAIGN_VIEW_AD_GROUP_COUNT_THRESHOLD) {
                            this.loadedAdGroupsByCampaignId[campaign.id] = adGroups;
                          }

                          campaign.children = adGroups;
                        } else {
                          campaign.children
                            = (this.loadedAdGroupsByCampaignId[campaign.id] || []).slice();
                        }

                        return campaign;
                      });

                    this.previousCampaigns = mainMenuCampaigns;
                    this.markAction('willRender');

                    return (
                      <StyledCampaignMainMenuView
                        {...props}
                        campaigns={mainMenuCampaigns}
                        mainMenuScenario={this.mainMenuScenario}
                        perfMarker={this.campaignMainMenuPerfMarker}
                        isLoadingData={false}
                      />
                    );
                  }
                }
              </CampaignOData>
            );
          }
        }
      </CampaignOData>
    );
  }
}

CampaignMainMenuAdapter.propTypes = {
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      AdvertiserCustomerID: PropTypes.number,
    }).isRequired,
  }).isRequired,
  appPerfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  updateWaterfallFilter: PropTypes.func,
  instrumentation: PropTypes.shape({
    mainMenuScenario: PropTypes.shape({
      create: PropTypes.func,
    }),
  }).isRequired,
  mode: PropTypes.string.isRequired,
};

CampaignMainMenuAdapter.defaultProps = {
  updateWaterfallFilter: {},
};

export const CampaignMainMenu
  = withServerContext(withInstrumentation(withI18nConsumer(withScope(withGlobalFilter(withHotKeys(CampaignMainMenuAdapter, ShortcutType.MainMenu))))));
